import styled from 'styled-components';
import { abstractBackgroundSoft } from './BackgroundPatterns';
import { techColors, textGlowStrong, terminalTextStyle, terminalHeaderStyle } from './TechRetroEffects';

export const AdminPageContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  ${abstractBackgroundSoft}
  color: ${techColors.textPrimary};
  ${terminalTextStyle}
  padding: max(72px, calc(env(safe-area-inset-top) + 48px)) 24px max(32px, env(safe-area-inset-bottom));
  box-sizing: border-box;
`;

export const Brand = styled.h1`
  margin: 0;
  font-size: 3.2em;
  letter-spacing: -0.02em;
  text-align: center;
  color: ${techColors.neonCyan};
  ${textGlowStrong(techColors.neonCyan)}
  ${terminalHeaderStyle}

  @media (max-width: 768px) {
    font-size: 2.4em;
  }
`;

export const Subtitle = styled.p`
  margin: 8px 0 28px;
  text-align: center;
  color: ${techColors.textSecondary};
  font-size: 0.95em;
  letter-spacing: 0.08em;
  text-transform: lowercase;
`;

export const Directory = styled.div`
  width: min(960px, 100%);
  margin: 0 auto;
`;

export const ListHead = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr) 168px 96px;
  gap: 16px;
  align-items: center;
  padding: 0 0 10px;
  color: ${techColors.textSecondary};
  font-size: 0.72em;
  letter-spacing: 0.1em;
  text-transform: lowercase;

  @media (max-width: 720px) {
    display: none;
  }
`;

export const Search = styled.input`
  width: 100%;
  margin-bottom: 18px;
  padding: 12px 14px;
  border: 1px solid ${techColors.borderSubtle};
  background: ${techColors.darkBgSecondary};
  color: ${techColors.textPrimary};
  font: inherit;
  outline: none;

  &::placeholder {
    color: ${techColors.textSecondary};
  }

  &:focus {
    border-color: ${techColors.neonCyan};
  }
`;

export const UserBlock = styled.div`
  border-top: 1px solid ${techColors.borderSubtle};
`;

export const UserRow = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr) 168px 96px;
  gap: 16px;
  align-items: center;
  padding: 14px 0 8px;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr) 96px;
    gap: 8px 12px;
  }
`;

export const UserName = styled.div`
  min-width: 0;
  color: ${techColors.textPrimary};
  font-size: 0.95em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UserEmail = styled.div`
  min-width: 0;
  color: ${techColors.textSecondary};
  font-size: 0.82em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 720px) {
    grid-column: 1 / 2;
  }
`;

export const UserMeta = styled.span`
  color: ${techColors.neonCyan};
  font-size: 0.75em;
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 720px) {
    grid-column: 1 / 2;
  }
`;

export const ToggleButton = styled.button`
  width: 96px;
  border: 1px solid ${techColors.borderSubtle};
  background: transparent;
  color: ${({ $off }) => ($off ? techColors.danger : techColors.textPrimary)};
  padding: 6px 0;
  cursor: pointer;
  font: inherit;
  font-size: 0.78em;
  letter-spacing: 0.04em;
  text-align: center;
  justify-self: end;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:hover:not(:disabled) {
    border-color: ${techColors.neonCyan};
  }

  @media (max-width: 720px) {
    grid-column: 2;
    grid-row: 1 / span 3;
    align-self: center;
  }
`;

export const UserActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 0 14px;
`;

export const GhostButton = styled.button`
  border: 1px solid ${techColors.borderSubtle};
  background: transparent;
  color: ${techColors.textSecondary};
  padding: 5px 10px;
  cursor: pointer;
  font: inherit;
  font-size: 0.72em;
  letter-spacing: 0.04em;

  &:hover:not(:disabled) {
    color: ${({ $danger }) => ($danger ? techColors.danger : techColors.textPrimary)};
    border-color: ${({ $danger }) => ($danger ? techColors.danger : techColors.neonCyan)};
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

export const BanForm = styled.form`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

export const BanInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid ${techColors.borderSubtle};
  background: ${techColors.darkBgSecondary};
  color: ${techColors.textPrimary};
  font: inherit;
  outline: none;

  &::placeholder {
    color: ${techColors.textSecondary};
  }

  &:focus {
    border-color: ${techColors.neonCyan};
  }
`;

export const SectionTitle = styled.h2`
  margin: 36px 0 12px;
  font-size: 0.78em;
  letter-spacing: 0.12em;
  text-transform: lowercase;
  color: ${techColors.textSecondary};
  font-weight: 500;
`;

export const CallRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px 96px;
  gap: 16px;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid ${techColors.borderSubtle};
`;

export const Notice = styled.p`
  margin: 0 0 18px;
  padding: 12px 14px;
  border: 1px solid ${techColors.neonCyan};
  color: ${techColors.neonCyan};
  font-size: 0.85em;
`;

export const Empty = styled.p`
  text-align: center;
  color: ${techColors.textSecondary};
`;
